<script setup lang="ts">
import { MContainerRow, MContainerSplit, MContainerColumn, TitleSmall } from "@xlabmedical/medic-web-framework";
import { ProjectModel } from "@xlabmedical/implant-planner";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { SegmentationStatus } from "@/models/SegmentationStatus";
import RgSegmentationStatus from "@/components/RgSegmentationStatus.vue";

const { t } = useI18n();

interface Props {
    project: ProjectModel;
    hasWeblinkOrder?: boolean;
    effectiveEditable?: boolean;
}

const props = withDefaults(defineProps<Props>(), { hasWeblinkOrder: false });

const getStatusInfo = computed(() => {
    const orderStatus = props.project.weblinkOrderData?.status;

    if (orderStatus) {
        return orderStatus;
    }

    return "-";
});

const getStatusColor = computed(() => {
    return props.project.weblinkOrderData?.statusColor;
});

const getProductionStepInfo = computed(() => {
    const productionStep = props.project.weblinkOrderData?.step;

    if (productionStep) {
        return productionStep;
    }
    return props.project.plannerStep || t("implant-planner.newProject");
});

const getAllowsUserEditingInfo = computed(() => (props.effectiveEditable ? t("medic-web-framework.yes") : t("medic-web-framework.no")));

const formattedVersionLabel = computed(() => {
    const value = t("medic-web-framework.version").trim();
    if (!value) return "";
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
});

const formattedFormatLabel = computed(() => {
    const value = t("medic-web-framework.format").trim();
    if (!value) return "";
    return value.charAt(0).toUpperCase() + value.slice(1);
});
</script>

<template>
    <MContainerColumn class="w-full gap-4">
        <MContainerRow class="px-4">
            <MContainerSplit>
                <MContainerColumn>
                    <TitleSmall>{{ $t("weblink.productionStep") }}</TitleSmall>
                    <span>{{ getProductionStepInfo }}</span>
                </MContainerColumn>
            </MContainerSplit>
            <MContainerSplit>
                <MContainerColumn v-show="hasWeblinkOrder">
                    <TitleSmall>{{ $t("shared.status") }}</TitleSmall>
                    <span v-if="getStatusColor" class="flex flex-row-wrap gap-2 items-center w-full">
                        <span :style="`background-color: ${getStatusColor} !important; `" class="transparent-dot" />
                        <span>{{ getStatusInfo }}</span>
                    </span>
                    <span v-else>{{ getStatusInfo }}</span>
                </MContainerColumn>
            </MContainerSplit>
            <MContainerSplit>
                <MContainerColumn v-show="hasWeblinkOrder">
                    <TitleSmall>{{ $t("web-rg.editable") }}</TitleSmall>
                    <span>{{ getAllowsUserEditingInfo }}</span>
                </MContainerColumn>
            </MContainerSplit>
        </MContainerRow>
        <MContainerRow class="px-4 pb-4 rg-border-bottom">
            <MContainerSplit>
                <MContainerColumn>
                    <TitleSmall>{{ $t("shared.date") }}</TitleSmall>
                    <span>{{ project.lastModified.toLocaleDateString() }}</span>
                </MContainerColumn>
            </MContainerSplit>
            <MContainerSplit>
                <MContainerColumn>
                    <TitleSmall>{{ $t("shared.time") }}</TitleSmall>
                    <span>{{ project.lastModified.toLocaleTimeString() }}</span>
                </MContainerColumn>
            </MContainerSplit>
            <MContainerSplit>
                <MContainerColumn>
                    <TitleSmall>{{ $t("shared.lastUpdateBy") }}</TitleSmall>
                    <span>{{ project.user }}</span>
                </MContainerColumn>
            </MContainerSplit>
        </MContainerRow>
        <MContainerRow class="px-4 -mt-1 items-center">
            <MContainerSplit class="items-center">
                <MContainerColumn class="justify-center">
                    <TitleSmall>{{ formattedFormatLabel }}</TitleSmall>
                    <span>{{ project.format }}</span>
                </MContainerColumn>
            </MContainerSplit>
            <MContainerSplit class="items-center">
                <MContainerColumn class="justify-center">
                    <TitleSmall>{{ formattedVersionLabel }}</TitleSmall>
                    <span>{{ project.versionCompany }}</span>
                </MContainerColumn>
            </MContainerSplit>
            <MContainerSplit class="items-center">
                <MContainerColumn class="justify-center">
                    <TitleSmall>{{ $t("web-rg.segmentation") }}</TitleSmall>
                    <RgSegmentationStatus :segmentation-status="project.segmentationInvokedByUser ? project.segmentationStatus : SegmentationStatus.NOT_STARTED" />
                </MContainerColumn>
            </MContainerSplit>
        </MContainerRow>
    </MContainerColumn>
</template>

<style scoped></style>
